// src/pages/PaymentPage.js
import React, { useEffect, useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Amplify } from 'aws-amplify';
import awsconfig from '../aws-exports';
import PaymentForm from '../components/features/Payment/PaymentForm';
import { fetchAuthSession } from 'aws-amplify/auth';
import './PaymentPage.css';

// App-wide Amplify config
Amplify.configure(awsconfig);

// Stripe publishable key (use env in prod)
const STRIPE_PUBLISHABLE_KEY =
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
  'pk_test_51RNAp3PFQWdJoEw84QEWGAngGJEcCNunykbYfGbUHQrEr6WmxXvu7vYyt2m4MZxzyqEZ0Ta37y2o2hbd4CP1bqyY009IA4KeFr';

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

// API base + recurring price id (override via .env)
const API_BASE =
  process.env.REACT_APP_API_BASE ||
  'https://bp7u0bhrc8.execute-api.eu-west-2.amazonaws.com/dev';

const PRICE_ID =
  process.env.REACT_APP_STRIPE_PRICE_ID ||
  'price_1RSkJpPFQWdJoEw8b2f9dU1v';

// tiny helper
async function http(url, init = {}) {
  const res = await fetch(url, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init.headers || {}) },
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : {};
  if (!res.ok || data.error) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

export default function PaymentPage() {
  // one-off card payment feedback
  const [cardError, setCardError] = useState(null);
  const [cardSuccess, setCardSuccess] = useState(false);

  // subscription helpers
  const [sessionId, setSessionId] = useState('');
  const [customerId, setCustomerId] = useState(
    localStorage.getItem('stripeCustomerId') || ''
  );
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [sessionOut, setSessionOut] = useState(null);
  const [statusOut, setStatusOut] = useState(null);
  const [precreatedCheckoutUrl, setPrecreatedCheckoutUrl] = useState(null);
  const [cachedIdToken, setCachedIdToken] = useState(null);

  // pick up session_id from success redirect if present
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get('session_id');
    if (id) setSessionId(id);
  }, []);

  // attempt to precreate a checkout session after we get an auth token
  useEffect(() => {
    let mounted = true;
    const precreate = async () => {
      try {
        const { tokens } = await fetchAuthSession();
        const idToken = tokens?.idToken?.toString();
        if (!idToken) return;
        setCachedIdToken(idToken);

        // call backend to create session (same body as startSubscription)
        const body = {
          priceId: PRICE_ID,
          successUrl: `${window.location.origin}/billing?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/billing`,
        };

        const { url } = await http(`${API_BASE}/create-checkout-session`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });

        if (mounted && url) setPrecreatedCheckoutUrl(url);
      } catch (err) {
        // silent: keep existing behaviour on button click
        console.debug('Precreate checkout failed:', err.message);
      }
    };

    precreate();
    return () => { mounted = false; };
  }, []); // run once on mount

  // ───────── subscription actions
  const startSubscription = async () => {
    try {
      setBusy(true);
      setNote('Creating checkout session…');

      // if we already precreated the session, redirect immediately
      if (precreatedCheckoutUrl) {
        window.location.assign(precreatedCheckoutUrl);
        return;
      }

      // fallback to original behaviour (create on demand)
      const idToken = cachedIdToken || (await fetchAuthSession()).tokens?.idToken?.toString();
      if (!idToken) { setNote('❌ Not signed in'); setBusy(false); return; }

      const { url } = await http(`${API_BASE}/create-checkout-session`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          priceId: PRICE_ID,
          successUrl: `${window.location.origin}/billing?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/billing`,
        }),
      });

      if (!url) throw new Error('No redirect URL from backend');
      window.location.assign(url);
    } catch (e) {
      setNote(`❌ ${e.message}`);
      setBusy(false);
    }
  };

  const lookupSession = async () => {
    if (!/^cs_/.test(sessionId || '')) {
      setNote('Enter a valid Checkout Session id (cs_…).');
      return;
    }
    try {
      setBusy(true);
      setNote('Looking up session…');
      const data = await http(
        `${API_BASE}/checkout/session?id=${encodeURIComponent(sessionId)}`
      );
      setSessionOut(data);
      if (data.customerId) {
        setCustomerId(data.customerId);
        localStorage.setItem('stripeCustomerId', data.customerId);
      }
      setNote('✅ Session loaded');
    } catch (e) {
      setNote(`❌ ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const getStatus = async () => {
    if (!/^cus_/.test(customerId || '')) {
      setNote('Enter a valid customer id (cus_…) or run Lookup session first.');
      return;
    }
    try {
      setBusy(true);
      setNote('Fetching subscription status…');
      const data = await http(
        `${API_BASE}/me/subscription?customerId=${encodeURIComponent(customerId)}`
      );
      setStatusOut(data);
      setNote('✅ Status loaded');
    } catch (e) {
      setNote(`❌ ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const openPortal = async () => {
    if (!/^cus_/.test(customerId || '')) {
      setNote('Enter a valid customer id (cus_…) or run Lookup session first.');
      return;
    }
    try {
      setBusy(true);
      setNote('Creating portal session…');

      // Get Cognito token for secured route (if needed)
      const { tokens } = await fetchAuthSession();
      const idToken = tokens?.idToken?.toString();

      const { url } = await http(`${API_BASE}/create-portal-session`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customerId,
          returnUrl: `${window.location.origin}/billing`,
        }),
      });
      window.location.assign(url);
    } catch (e) {
      setNote(`❌ ${e.message}`);
      setBusy(false);
    }
  };

  return (
    <main className="payment-page">
      <header className="payment-header">
        <h1>Payments & Subscriptions</h1>
      </header>

      {/* One-off Card Payment */}
      <section className="card-section">
        <div className="card">
          <h2>Card payment</h2>
          <p className="muted">Amount: £10.00</p>

          <Elements
            stripe={stripePromise}
            options={{ appearance: { theme: 'stripe' } }}
          >
            <PaymentForm
              amount={1000} // pence
              onSuccess={() => {
                setCardSuccess(true);
                setCardError(null);
              }}
              onError={(m) => {
                console.error('Payment error:', m);
                setCardError(m);
              }}
            />
          </Elements>

          {cardError && <p className="alert error">{cardError}</p>}
          {cardSuccess && <p className="alert success">Payment successful!</p>}
        </div>
      </section>

      {/* Subscription Controls */}
      <section className="sub-section">
        <div className="card">
          <h2>Subscription</h2>

          <div className="row">
            <button className="btn" onClick={startSubscription} disabled={busy}>
              Start subscription checkout
            </button>
          </div>

          <div className="field">
            <label htmlFor="sessionId">Checkout Session ID (cs_…)</label>
            <input
              id="sessionId"
              type="text"
              value={sessionId}
              onChange={(e) => setSessionId(e.target.value)}
              placeholder="cs_test_…"
            />
          </div>

          <div className="row">
            <button className="btn" onClick={lookupSession} disabled={busy}>
              Lookup session & save customer
            </button>
          </div>

          <div className="field">
            <label htmlFor="customerId">Customer ID (cus_…)</label>
            <input
              id="customerId"
              type="text"
              value={customerId}
              onChange={(e) => {
                setCustomerId(e.target.value);
                if (e.target.value) {
                  localStorage.setItem('stripeCustomerId', e.target.value);
                }
              }}
              placeholder="cus_…"
            />
          </div>

          <div className="row">
            <button className="btn" onClick={getStatus} disabled={busy}>
              Get subscription status
            </button>
            <button className="btn secondary" onClick={openPortal} disabled={busy}>
              Open billing portal
            </button>
          </div>

          {note && <p className="muted" style={{ marginTop: 8 }}>{note}</p>}

          {sessionOut && (
            <>
              <h3 className="split">Checkout Session</h3>
              <pre className="pre">{JSON.stringify(sessionOut, null, 2)}</pre>
            </>
          )}

          {statusOut && (
            <>
              <h3 className="split">Subscription Status</h3>
              <pre className="pre">{JSON.stringify(statusOut, null, 2)}</pre>
            </>
          )}
        </div>
      </section>
    </main>
  );
}